<template>
  <div :class="`news-wrapper ${status}`">
    <div class="overlay" @click="handleClose"></div>
    <div class="news-navigator">
      <p class="news-title">{{ $t('menu.latest-news') }}</p>
      <div class="news-iframe">
        <m-iframe :lazy="false" :src="iframeSrc" no-cache />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, onMounted, onUnmounted, ref, watch} from '@vue/composition-api'

import MIframe from '@/components/atom/MIframe.vue'
import {formatToISODate} from '@/utils/date-helper'
import {hashString} from '@/utils/utils'

export default defineComponent({
  name: 'LastNews',
  components: {MIframe},
  props: {
    branch: {
      type: String,
      required: true,
      default: 'es',
    },
    display: {
      type: Boolean,
      required: false,
    },
  },
  setup(props, {root, emit}) {
    const status = ref('')

    const iframeSrc = `${process.env.VUE_APP_NEWS_URL}/${props.branch === 'us' ? 'es' : props.branch}/news?theme=light`

    function showNews() {
      status.value = 'fade-in'
    }

    function hideNews() {
      status.value = 'fade-out'
      setTimeout(() => (status.value = ''), 490)
    }

    function handleClose() {
      emit('hideNews')
    }

    function getIframeMessage(event: any) {
      if (event.origin !== process.env.VUE_APP_NEWS_URL) {
        return
      }

      const userLastNewsKey = `last-news-${hashString(root.$store.state.cognitoUser.username)}`
      const featureDateFromEvent = formatToISODate(event.data)
      const featureDateFromStore = localStorage?.getItem(userLastNewsKey)

      if (featureDateFromEvent !== featureDateFromStore && featureDateFromStore !== 'disable-news') {
        emit('showNews')
        localStorage?.setItem(userLastNewsKey, featureDateFromEvent)
      } else if (!featureDateFromStore) {
        localStorage?.setItem(userLastNewsKey, featureDateFromEvent)
      }
    }

    onMounted(() => {
      window.addEventListener('message', getIframeMessage)
    })

    onUnmounted(() => {
      window.removeEventListener('message', getIframeMessage)
    })

    watch(
      () => props.display,
      display => (display ? showNews() : hideNews()),
    )

    return {
      handleClose,
      iframeSrc,
      status,
    }
  },
})
</script>

<style scoped>
.overlay {
  width: 100%;
  height: 100%;
  background-color: #222;
  opacity: 0.46;
  cursor: pointer;
}

.news-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  width: 100%;
  height: 100vh;
  display: none;
}

.news-navigator {
  position: absolute;
  top: 82px !important;
  right: 10px;
  z-index: 101;
  overflow: hidden;
  width: 400px;
  border-radius: 6px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
}

.fade-in,
.fade-out {
  display: block;
}

.fade-in .news-navigator {
  animation: showSection 0.5s ease-in-out;
}

.fade-out .news-navigator {
  animation: hideSection 0.5s ease-in-out;
}

.news-title {
  background-color: #dc182d;
  color: white;
  font-family: 'ProximaNovaExCnSBold', serif;
  font-size: 24px;
  margin: 0;
  padding: 10px;
  position: fixed;
  text-align: center;
  text-transform: uppercase;
  width: 100%;
}

.news-iframe {
  background-color: #fff;
  height: calc(100vh - 138px);
  padding-top: 56px;
  overflow-y: auto;
}

@keyframes showSection {
  0% {
    transform: translateX(100%);
    visibility: hidden;
  }
  100% {
    transform: translateX(0);
    visibility: visible;
  }
}

@keyframes hideSection {
  0% {
    transform: translateX(0);
    visibility: visible;
  }
  100% {
    transform: translateX(100%);
    visibility: hidden;
  }
}
</style>
